import { useCallback, useEffect, useMemo, useState } from 'react';
import { getDocStore } from '../../store/doc-store.ts';
import type { SyncStatus } from '../../store/sync-provider.ts';
import {
  type AccessProbeTarget,
  type AccessService,
  accessProbeTargets,
  accessRouteRequiresSignIn,
} from './access-session.ts';

const LABELS: Record<SyncStatus, string> = {
  disabled: 'Local',
  connecting: 'Connecting…',
  connected: 'Online',
  disconnected: 'Offline',
};

const TONE: Record<SyncStatus, string> = {
  disabled: 'ui-chip',
  connecting: 'ui-chip ui-chip-warning',
  connected: 'ui-chip ui-chip-success',
  disconnected: 'ui-chip ui-chip-danger',
};

/**
 * Tiny status pill in the header that reflects the websocket sync state.
 *
 * - **Local** — no sync server configured (default in dev/tests).
 * - **Connecting…** — handshake in progress.
 * - **Online** — websocket open and Yjs in sync with the room.
 * - **Offline** — disconnected; the underlying provider is retrying
 *   with exponential backoff.
 */
export function SyncStatusBadge({
  apiUrl = import.meta.env.VITE_API_URL,
  syncUrl = import.meta.env.VITE_SYNC_URL,
}: {
  apiUrl?: string;
  syncUrl?: string;
} = {}): JSX.Element {
  const status = getDocStore().useSyncStatus();
  const targets = useMemo(() => accessProbeTargets(apiUrl, syncUrl), [apiUrl, syncUrl]);
  const [signInRequired, setSignInRequired] = useState<AccessService[]>([]);
  const probeAccess = useCallback(async (): Promise<void> => {
    const results = await Promise.all(
      targets.map(async (target) => ({
        id: target.id,
        required: await accessRouteRequiresSignIn(target.url),
      })),
    );
    setSignInRequired((current) => {
      const next = new Set(current);
      for (const result of results) {
        if (result.required === true) next.add(result.id);
        if (result.required === false) next.delete(result.id);
      }
      return [...next];
    });
  }, [targets]);

  useEffect(() => {
    if (targets.length === 0) return;
    void probeAccess();
    const interval = window.setInterval(() => void probeAccess(), 5 * 60_000);
    window.addEventListener('focus', probeAccess);
    return () => {
      window.clearInterval(interval);
      window.removeEventListener('focus', probeAccess);
    };
  }, [probeAccess, targets.length]);

  useEffect(() => {
    if (status === 'connecting' || status === 'disconnected') void probeAccess();
  }, [probeAccess, status]);

  const openSignIn = (target: AccessProbeTarget): void => {
    window.open(target.url, '_blank', 'noopener,noreferrer');
  };

  return (
    <div className="flex items-center gap-2">
      {targets
        .filter((target) => signInRequired.includes(target.id))
        .map((target) => (
          <button
            key={target.id}
            type="button"
            className="ui-button ui-button-danger h-8 px-2 text-xs"
            onClick={() => openSignIn(target)}
          >
            Sign in: {target.label}
          </button>
        ))}
      <span
        data-testid="sync-status-badge"
        data-status={status}
        className={`${TONE[status]} text-[10px] uppercase tracking-[0.16em]`}
      >
        {LABELS[status]}
      </span>
    </div>
  );
}
